import { StructureEditPermissionSet } from '../../../@types';
import { PropertyType } from '../../../configs/enums';

/** Namespaces this page's tab/panel ids — see KeepMountedTabPanel's idPrefix. */
export const STRUCTURE_TAB_ID_PREFIX = 'structure';

export type StructureTabKey = 'details' | 'parts' | 'related' | 'admin';

export interface StructureTabDescriptor {
  key: StructureTabKey;
  label: string;
}

export const STRUCTURE_TABS: StructureTabDescriptor[] = [
  { key: 'details', label: 'Details' },
  { key: 'parts', label: 'Parts' },
  { key: 'related', label: 'Related' },
  { key: 'admin', label: 'Administration' },
];

export const DEFAULT_STRUCTURE_TAB: StructureTabKey = 'details';

export interface StructureTabVisibilityContext {
  /** Create mode — the tab strip is suppressed entirely, as on Part Type. */
  isNew: boolean;
  /**
   * The page is rendered inside another page's modal (the location editor on job
   * creation). A tab strip there would compete with the host page's own chrome.
   */
  isEmbedded: boolean;
  /** Property-type codes the structure's type grants. */
  propertyTypes: string[];
  permissions: StructureEditPermissionSet;
  /**
   * The client/customer variant has no Capacity section, so a type that grants
   * only Allocations gives it an empty location band. Merge is *not* gated this
   * way — that page offered it too, to anyone holding the permission.
   */
  isClientCustomer?: boolean;
}

/**
 * Whether the Details tab should carry its Location band.
 *
 * Location was a tab of its own until the four panels turned out to be a short
 * read that made the user pay a click to see where a structure is — the one
 * thing most of them open a structure to check. It is now a labelled band at
 * the foot of Details, and this is the same condition that used to decide
 * whether the tab appeared at all.
 */
export const hasStructureLocationContent = ({
  propertyTypes,
  isClientCustomer,
}: Pick<StructureTabVisibilityContext, 'propertyTypes' | 'isClientCustomer'>) =>
  propertyTypes.includes(PropertyType.Address) ||
  propertyTypes.includes(PropertyType.Contact) ||
  propertyTypes.includes(PropertyType.TradingHours) ||
  (!isClientCustomer && propertyTypes.includes(PropertyType.Allocations));

const hasPartsContent = ({ propertyTypes, permissions }: StructureTabVisibilityContext) =>
  propertyTypes.includes(PropertyType.Parts) && !!permissions.hasViewPartsEditPermission;

const hasRelatedContent = ({ propertyTypes, permissions }: StructureTabVisibilityContext) =>
  !!permissions.hasViewChildrenEditPermission ||
  !!permissions.hasViewShipmentsEditPermission ||
  ((propertyTypes.includes(PropertyType.Service) ||
    propertyTypes.includes(PropertyType.ServicesAssignment)) &&
    !!permissions.hasViewServicesEditPermission);

const hasAdminContent = ({ permissions }: StructureTabVisibilityContext) =>
  !!permissions.hasAllowMergeEditPermission;

export type StructureSectionVisibility = Record<StructureTabKey, boolean>;

/**
 * Which sections have anything in them.
 *
 * Every rule here restates the condition that already gated the equivalent
 * stacked section, so a structure sees exactly the sections it saw before the
 * tabs went in — no more, no fewer.
 *
 * This is deliberately separate from `getVisibleStructureTabs`: create mode and
 * the embedded modal render no tab strip but must still render their sections,
 * so "should this content exist" and "should it have a tab" are different
 * questions. Details is always true — it holds the name, which every structure
 * has, plus the Location band when the structure's type grants any of it.
 */
export const getStructureSectionVisibility = (
  context: StructureTabVisibilityContext
): StructureSectionVisibility => ({
  details: true,
  parts: hasPartsContent(context),
  related: hasRelatedContent(context),
  admin: hasAdminContent(context),
});

/**
 * The tab set to render in the strip, in display order. Empty in create and
 * embedded modes, where the page falls back to one stacked column.
 */
export const getVisibleStructureTabs = (
  context: StructureTabVisibilityContext
): StructureTabKey[] => {
  if (context.isNew || context.isEmbedded) {
    return [];
  }

  const sections = getStructureSectionVisibility(context);
  return STRUCTURE_TABS.filter(({ key }) => sections[key]).map(({ key }) => key);
};

export const isStructureTabKey = (value: string | null): value is StructureTabKey =>
  !!value && STRUCTURE_TABS.some(({ key }) => key === value);
